import { useState } from 'react'
import {Star, AlertTriangle, BookOpen, ChevronRight, Tag, Lock, Globe, Sliders, Info, Save, Check, Plus, Trash2, Pencil, X, FileText} from 'lucide-react'
import AdminLayout from '@/components/AdminLayout'
import { usePlatformSettings, EvalAspect, CaseCategory } from '@/hooks/usePlatformSettings'

type Section = 'general' | 'evaluaciones' | 'casos' | 'capacitaciones' | 'legales' | 'seguridad' | 'plataforma'

const sections: { key: Section; label: string; icon: React.ElementType; desc: string }[] = [
  { key: 'general', label: 'General', icon: Sliders, desc: 'Nombre, descripción y datos generales de la plataforma' },
  { key: 'evaluaciones', label: 'Evaluaciones', icon: Star, desc: 'Aspectos y escala de evaluación de colaboradores' },
  { key: 'casos', label: 'Categorías de casos', icon: Tag, desc: 'Categorías de reportes laborales (empresa y colaborador)' },
  { key: 'capacitaciones', label: 'Capacitaciones', icon: BookOpen, desc: 'Tipos, modalidades y estados de capacitaciones' },
  { key: 'legales', label: 'Documentos legales', icon: FileText, desc: 'Aviso de privacidad, reglamentos, políticas y términos' },
  { key: 'seguridad', label: 'Seguridad y acceso', icon: Lock, desc: 'Credenciales de administrador y control de acceso' },
  { key: 'plataforma', label: 'Plataforma', icon: Globe, desc: 'Información de versión y estado del sistema' },
]

function slugify(label: string): string {
  return label.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || `item_${Date.now()}`
}

function SaveBanner({ saved }: { saved: boolean }) {
  if (!saved) return null
  return (
    <div className="mb-4 flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-700">
      <Check size={15} className="shrink-0" />
      Configuración guardada correctamente.
    </div>
  )
}

const SAVE_BTN = 'flex items-center gap-2 rounded-lg bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-amber-500 disabled:opacity-50'
const INPUT = 'w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100'

/* ───── Editor genérico de listas de categorías (value/label) ───── */
function CategoryEditor({
  title,
  items,
  accent,
  onChange,
}: {
  title: string
  items: CaseCategory[]
  accent: 'red' | 'amber'
  onChange: (items: CaseCategory[]) => void
}) {
  const [newLabel, setNewLabel] = useState('')
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [editValue, setEditValue] = useState('')

  const palette = accent === 'red'
    ? 'border-red-100 bg-red-50 text-red-700'
    : 'border-amber-100 bg-amber-50 text-amber-700'

  const add = () => {
    const label = newLabel.trim()
    if (!label) return
    const value = slugify(label)
    if (items.some((i) => i.value === value)) return
    onChange([...items, { value, label }])
    setNewLabel('')
  }

  const saveEdit = (i: number) => {
    const label = editValue.trim()
    if (!label) return
    const next = items.map((it, idx) => (idx === i ? { ...it, label } : it))
    onChange(next)
    setEditIndex(null)
    setEditValue('')
  }

  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i))

  return (
    <div>
      <p className="mb-2 text-xs font-semibold text-slate-700">{title} ({items.length})</p>
      <div className="space-y-2">
        {items.map((c, i) => (
          <div key={c.value} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${palette}`}>
            <AlertTriangle size={12} className="shrink-0" />
            {editIndex === i ? (
              <>
                <input
                  autoFocus
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(i) }}
                  className="flex-1 rounded border border-slate-300 bg-white px-2 py-1 text-slate-800 outline-none"
                />
                <button onClick={() => saveEdit(i)} className="rounded p-1 text-emerald-600 hover:bg-white"><Check size={14} /></button>
                <button onClick={() => { setEditIndex(null); setEditValue('') }} className="rounded p-1 text-slate-400 hover:bg-white"><X size={14} /></button>
              </>
            ) : (
              <>
                <span className="flex-1 font-medium">{c.label}</span>
                <button onClick={() => { setEditIndex(i); setEditValue(c.label) }} className="rounded p-1 text-slate-500 hover:bg-white" title="Editar"><Pencil size={13} /></button>
                <button onClick={() => remove(i)} className="rounded p-1 text-red-500 hover:bg-white" title="Eliminar"><Trash2 size={13} /></button>
              </>
            )}
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add() }}
          placeholder="Nueva categoría…"
          className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs outline-none focus:border-amber-400"
        />
        <button onClick={add} className="flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800">
          <Plus size={13} /> Añadir
        </button>
      </div>
    </div>
  )
}

/* ───── Editor genérico de listas de strings (tipos / modalidades) ───── */
function StringListEditor({
  title,
  items,
  color,
  onChange,
}: {
  title: string
  items: string[]
  color: string
  onChange: (items: string[]) => void
}) {
  const [val, setVal] = useState('')
  const add = () => {
    const v = val.trim()
    if (!v || items.includes(v)) return
    onChange([...items, v])
    setVal('')
  }
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i))

  return (
    <div>
      <p className="mb-2 text-xs font-semibold text-slate-700">{title}</p>
      <div className="flex flex-wrap gap-2">
        {items.map((item, i) => (
          <span key={item} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${color}`}>
            {item}
            <button onClick={() => remove(i)} className="text-current/70 hover:text-red-600"><X size={12} /></button>
          </span>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add() }}
          placeholder="Nuevo elemento…"
          className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs outline-none focus:border-amber-400"
        />
        <button onClick={add} className="flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800">
          <Plus size={13} /> Añadir
        </button>
      </div>
    </div>
  )
}

function AdminConfiguracion() {
  const [activeSection, setActiveSection] = useState<Section>('general')
  const [saved, setSaved] = useState(false)
  const { settings, setSettings, loading, saving, persist } = usePlatformSettings()

  // Estado del editor de aspectos de evaluación
  const [newAspect, setNewAspect] = useState('')

  const handleSave = async () => {
    const ok = await persist(settings)
    if (ok) {
      setSaved(true)
      setTimeout(() => setSaved(false), 3500)
    }
  }

  const update = <K extends keyof typeof settings>(field: K, value: typeof settings[K]) => {
    setSettings({ ...settings, [field]: value })
  }

  const addAspect = () => {
    const label = newAspect.trim()
    if (!label) return
    const key = slugify(label)
    if (settings.evaluationAspects.some((a) => a.key === key)) return
    update('evaluationAspects', [...settings.evaluationAspects, { key, label, description: '' } as EvalAspect])
    setNewAspect('')
  }

  const removeAspect = (key: string) => {
    update('evaluationAspects', settings.evaluationAspects.filter((a) => a.key !== key))
  }

  if (loading) {
    return (
      <AdminLayout title="Configuración del sistema" subtitle="Cargando configuración…">
        <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />)}</div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout title="Configuración del sistema" subtitle="Ajusta parámetros de negocio, evaluaciones, casos, capacitaciones y seguridad">
      <div className="flex overflow-hidden rounded-xl border border-slate-200 bg-white" style={{ minHeight: 'calc(100vh - 12rem)' }}>
        {/* Panel de secciones */}
        <aside className="hidden w-56 shrink-0 overflow-y-auto border-r border-slate-200 bg-white p-3 md:flex md:flex-col gap-1">
          {sections.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition ${
                activeSection === key ? 'bg-amber-50 text-amber-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              <Icon size={15} />
              {label}
              {activeSection === key && <ChevronRight size={13} className="ml-auto text-amber-500" />}
            </button>
          ))}
        </aside>

        {/* Contenido de sección */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4 md:hidden">
            <select
              value={activeSection}
              onChange={(e) => setActiveSection(e.target.value as Section)}
              className="w-full rounded-lg border border-slate-200 bg-white py-2.5 px-3 text-sm outline-none focus:border-amber-400"
            >
              {sections.map(({ key, label }) => <option key={key} value={key}>{label}</option>)}
            </select>
          </div>

          <SaveBanner saved={saved} />

          {/* ── GENERAL ── */}
          {activeSection === 'general' && (
            <div className="space-y-6 max-w-2xl">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Configuración general</h2>
                <p className="mt-0.5 text-sm text-slate-500">Nombre, descripción y contacto de soporte de la plataforma.</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">Nombre de la plataforma</label>
                  <input value={settings.platformName} onChange={(e) => update('platformName', e.target.value)} className={INPUT} />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">Descripción</label>
                  <textarea value={settings.platformDesc} onChange={(e) => update('platformDesc', e.target.value)} rows={3} className={`${INPUT} resize-none`} />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">Correo de soporte</label>
                  <input value={settings.supportEmail} onChange={(e) => update('supportEmail', e.target.value)} type="email" className={INPUT} />
                </div>
                <button onClick={handleSave} disabled={saving} className={SAVE_BTN}>
                  <Save size={14} /> {saving ? 'Guardando…' : 'Guardar cambios'}
                </button>
              </div>
            </div>
          )}

          {/* ── EVALUACIONES ── */}
          {activeSection === 'evaluaciones' && (
            <div className="space-y-6 max-w-2xl">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Configuración de evaluaciones</h2>
                <p className="mt-0.5 text-sm text-slate-500">Escala y aspectos de evaluación de desempeño de colaboradores.</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-5">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">Puntuación mínima</label>
                    <input
                      value={settings.minEvalScore}
                      onChange={(e) => update('minEvalScore', (Number(e.target.value) || 0) as never)}
                      type="number" min={1} max={9} className={INPUT}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">Puntuación máxima</label>
                    <input
                      value={settings.maxEvalScore}
                      onChange={(e) => update('maxEvalScore', (Number(e.target.value) || 0) as never)}
                      type="number" min={2} max={100} className={INPUT}
                    />
                  </div>
                </div>

                <div>
                  <p className="mb-2 text-xs font-semibold text-slate-700">Aspectos de evaluación activos ({settings.evaluationAspects.length})</p>
                  <div className="space-y-2">
                    {settings.evaluationAspects.map((a) => (
                      <div key={a.key} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                        <div className="h-2 w-2 rounded-full bg-amber-400 shrink-0" />
                        <span className="flex-1 font-medium">{a.label}</span>
                        <button onClick={() => removeAspect(a.key)} className="rounded p-1 text-red-500 hover:bg-white" title="Eliminar aspecto"><Trash2 size={13} /></button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <input
                      value={newAspect}
                      onChange={(e) => setNewAspect(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') addAspect() }}
                      placeholder="Nuevo aspecto de evaluación…"
                      className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs outline-none focus:border-amber-400"
                    />
                    <button onClick={addAspect} className="flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800">
                      <Plus size={13} /> Añadir
                    </button>
                  </div>
                </div>

                <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-100 p-3 text-xs text-blue-700">
                  <Info size={13} className="mt-0.5 shrink-0" />
                  Los cambios se guardan en la base de datos de la plataforma y se aplican para todos los administradores.
                </div>
                <button onClick={handleSave} disabled={saving} className={SAVE_BTN}>
                  <Save size={14} /> {saving ? 'Guardando…' : 'Guardar cambios'}
                </button>
              </div>
            </div>
          )}

          {/* ── CASOS ── */}
          {activeSection === 'casos' && (
            <div className="space-y-6 max-w-2xl">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Categorías de reportes laborales</h2>
                <p className="mt-0.5 text-sm text-slate-500">Añade, edita o elimina las categorías disponibles para apertura de casos.</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-6">
                <CategoryEditor
                  title="Empresa → Colaborador"
                  accent="red"
                  items={settings.caseCategoriesToCollaborator}
                  onChange={(items) => update('caseCategoriesToCollaborator', items)}
                />
                <CategoryEditor
                  title="Colaborador → Empresa"
                  accent="amber"
                  items={settings.caseCategoriesToCompany}
                  onChange={(items) => update('caseCategoriesToCompany', items)}
                />
                <button onClick={handleSave} disabled={saving} className={SAVE_BTN}>
                  <Save size={14} /> {saving ? 'Guardando…' : 'Guardar cambios'}
                </button>
              </div>
            </div>
          )}

          {/* ── CAPACITACIONES ── */}
          {activeSection === 'capacitaciones' && (
            <div className="space-y-6 max-w-2xl">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Configuración de capacitaciones</h2>
                <p className="mt-0.5 text-sm text-slate-500">Tipos y modalidades disponibles en la plataforma.</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-6">
                <StringListEditor
                  title="Tipos de capacitación"
                  color="bg-violet-50 border-violet-100 text-violet-700"
                  items={settings.trainingTypes}
                  onChange={(items) => update('trainingTypes', items)}
                />
                <StringListEditor
                  title="Modalidades"
                  color="bg-blue-50 border-blue-100 text-blue-700"
                  items={settings.trainingModalities}
                  onChange={(items) => update('trainingModalities', items)}
                />
                <div>
                  <p className="mb-2 text-xs font-semibold text-slate-700">Estados de publicación (fijos del sistema)</p>
                  <div className="flex flex-wrap gap-2">
                    {['Borrador', 'Publicada', 'Finalizada', 'Cancelada'].map((s) => (
                      <span key={s} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700">{s}</span>
                    ))}
                  </div>
                </div>
                <button onClick={handleSave} disabled={saving} className={SAVE_BTN}>
                  <Save size={14} /> {saving ? 'Guardando…' : 'Guardar cambios'}
                </button>
              </div>
            </div>
          )}

          {/* ── DOCUMENTOS LEGALES ── */}
          {activeSection === 'legales' && (
            <div className="space-y-6 max-w-2xl">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Documentos legales</h2>
                <p className="mt-0.5 text-sm text-slate-500">Edita el contenido de los documentos que se muestran a los usuarios en la plataforma. Los cambios se aplican de inmediato.</p>
              </div>
              {([
                { field: 'privacyPolicy' as const, label: 'Aviso de Privacidad', desc: 'Visible al registrarse y en los links de la plataforma.' },
                { field: 'reportsPolicy' as const, label: 'Reglamento de Reportes y Evidencias', desc: 'Explica cómo se gestionan los casos y evidencias.' },
                { field: 'evaluationsPolicy' as const, label: 'Política de Evaluaciones y Reputación Laboral', desc: 'Reglas sobre las evaluaciones entre empresas y colaboradores.' },
                { field: 'termsAndConditions' as const, label: 'Términos y Condiciones', desc: 'Los términos que el usuario acepta al crear su cuenta.' },
              ]).map(({ field, label, desc }) => (
                <div key={field} className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                  </div>
                  <textarea
                    value={settings[field] ?? ''}
                    onChange={(e) => update(field, e.target.value as never)}
                    rows={8}
                    className={`${INPUT} resize-y font-mono text-xs leading-relaxed`}
                    placeholder={`Escribe aquí el contenido de: ${label}`}
                  />
                </div>
              ))}
              <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-100 p-3 text-xs text-blue-700">
                <Info size={13} className="mt-0.5 shrink-0" />
                El contenido se guarda en la base de datos y los usuarios lo verán en tiempo real al hacer clic en los enlaces correspondientes.
              </div>
              <button onClick={handleSave} disabled={saving} className={SAVE_BTN}>
                <Save size={14} /> {saving ? 'Guardando…' : 'Guardar documentos'}
              </button>
            </div>
          )}

          {/* ── SEGURIDAD ── */}
          {activeSection === 'seguridad' && (
            <div className="space-y-6 max-w-2xl">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Seguridad y acceso</h2>
                <p className="mt-0.5 text-sm text-slate-500">Credenciales del administrador y configuración de acceso al sistema.</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
                <div className="flex items-start gap-3 rounded-lg bg-amber-50 border border-amber-200 p-4">
                  <Lock size={16} className="mt-0.5 shrink-0 text-amber-600" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Cuenta de administrador</p>
                    <p className="mt-1 text-xs text-amber-700">
                      El acceso al panel Sistema utiliza credenciales configuradas directamente en el código.
                      Para mayor seguridad, se recomienda migrar a un modelo de roles gestionado por Lumi Auth en una versión futura.
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-700 mb-2">Permisos de colecciones configurados</p>
                  <div className="overflow-hidden rounded-lg border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 border-b border-slate-100">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-slate-500">Colección</th>
                          <th className="px-3 py-2 text-left font-semibold text-slate-500">Lectura</th>
                          <th className="px-3 py-2 text-left font-semibold text-slate-500">Escritura</th>
                          <th className="px-3 py-2 text-left font-semibold text-slate-500">Eliminación</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {[
                          { name: 'profiles', r: 'Sin restricción', w: 'Solo creador', d: 'Admin' },
                          { name: 'companies', r: 'Sin restricción', w: 'Solo creador', d: 'Admin' },
                          { name: 'collaborators', r: 'Sin restricción', w: 'Solo creador', d: 'Admin' },
                          { name: 'vacancies', r: 'Sin restricción', w: 'Solo creador', d: 'Solo creador' },
                          { name: 'applications', r: 'Sin restricción', w: 'Sin restricción', d: 'Solo creador' },
                          { name: 'links', r: 'Sin restricción', w: 'Sin restricción', d: 'Solo creador' },
                          { name: 'evaluations', r: 'Sin restricción', w: 'Solo creador', d: 'Solo creador' },
                          { name: 'cases', r: 'Sin restricción', w: 'Sin restricción', d: 'Solo creador' },
                          { name: 'trainings', r: 'Sin restricción', w: 'Admin', d: 'Admin' },
                          { name: 'enrollments', r: 'Sin restricción', w: 'Solo creador', d: 'Solo creador' },
                          { name: 'notifications', r: 'Sin restricción', w: 'Admin', d: 'Admin' },
                          { name: 'platform_settings', r: 'Sin restricción', w: 'Sin restricción', d: 'Admin' },
                        ].map(({ name, r, w, d }) => (
                          <tr key={name} className="hover:bg-slate-50">
                            <td className="px-3 py-2 font-mono font-medium text-slate-700">{name}</td>
                            <td className="px-3 py-2 text-emerald-600">{r}</td>
                            <td className="px-3 py-2 text-amber-600">{w}</td>
                            <td className="px-3 py-2 text-red-500">{d}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── PLATAFORMA ── */}
          {activeSection === 'plataforma' && (
            <div className="space-y-6 max-w-2xl">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Información de la plataforma</h2>
                <p className="mt-0.5 text-sm text-slate-500">Versión, estado del sistema y módulos activos.</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
                {[
                  { label: 'Nombre del producto', value: 'Beeznz Work' },
                  { label: 'Versión', value: '1.0.0-beta' },
                  { label: 'Stack', value: 'React + Vite + TypeScript + Tailwind CSS + Lumi SDK' },
                  { label: 'Base de datos', value: 'MongoDB (Lumi Entities)' },
                  { label: 'Autenticación', value: 'Lumi Auth (OTP + contraseña)' },
                  { label: 'Entorno', value: 'Producción (Lumi Platform)' },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-start justify-between gap-4 border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                    <span className="text-xs font-semibold text-slate-500">{label}</span>
                    <span className="text-right text-xs font-medium text-slate-800">{value}</span>
                  </div>
                ))}
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <p className="mb-3 text-xs font-semibold text-slate-700">Módulos activos (12 colecciones)</p>
                <div className="flex flex-wrap gap-2">
                  {['profiles', 'companies', 'collaborators', 'vacancies', 'applications', 'links', 'evaluations', 'cases', 'trainings', 'enrollments', 'notifications', 'platform_settings'].map((m) => (
                    <span key={m} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}

export default AdminConfiguracion
